/**
 * @fileoverview Locations that have been retired. Nothing here is deleted; each can be put
 * back with one press. The same page the plants have, for the same reason —
 * a bed you stopped using is still where those tomatoes grew.
 *
 * A restore that fails is said in a toast as well as above the tiles, which
 * may be scrolled out of sight; an archive that could not be read says so
 * rather than "Nothing archived". A failure on our side (D31,
 * lib/serverFailure) says the standard sentence instead, reported to
 * Sentry, with the support address as a link.
 *
 * Where: the route /beds/archive (App.tsx; /locations/archive before D56, sent on by LocationsRedirect), from the Locations
 * toolbar's Archive link.
 * API: GET /locations?status=0 (getArchivedLocations); PATCH
 * /locations/:id/status { status: 1 } (LocationsContext restoreLocation).
 * Settings: ARCHIVED_LOCATIONS_QUERY_KEY (config/tunables/garden).
 * Words (en/es): locations.archive.* (title, intro, backToLocations,
 * loading, loadFailed, empty, restore, restoreFailed).
 *
 * @module ArchivedLocations
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useLocationsContext } from '@/shared/context/LocationsContextDefinition';
import { getArchivedLocations, type Location } from '@/shared/api/locations';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { useToast } from '@/shared/components/ui/use-toast';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { LocationTile } from './LocationTile';
import { ARCHIVED_LOCATIONS_QUERY_KEY } from '@/config/tunables/garden';

/** The archive page: the gardener's retired beds, each with a way back. */
export const ArchivedLocations: React.FC = () => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  const userId = mysqlUser?.user_id ?? null;
  const { restoreLocation } = useLocationsContext();
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: locations = [], isLoading, isError, error: loadError, refetch } = useQuery({
    // The language is in the key: each bed's lines and plants' names come in it (B72), so a switch reads them again.
    queryKey: [ARCHIVED_LOCATIONS_QUERY_KEY, userId, language],
    queryFn: getArchivedLocations,
    enabled: userId !== null,
  });

  /**
   * Puts a bed back among the live ones, then reads the archive again so it
   * leaves this page. A failure is said above the tiles and in a toast, in
   * the server's words where it refused, never axios's.
   *
   * @param location - The archived bed.
   */
  const restore = async (location: Location): Promise<void> => {
    setRestoringId(location.id);
    setError(null);
    try {
      await restoreLocation(location.id);
      await refetch();
    } catch (err) {
      const message = errorMessage(err, t('locations.archive.restoreFailed'));
      setError(message);
      toast({ title: message, variant: 'destructive' });
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="p-4">
      <Link
        to="/beds"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('locations.archive.backToLocations')}
      </Link>
      <h1 className="text-2xl font-semibold font-body">{t('locations.archive.title')}</h1>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">{t('locations.archive.intro')}</p>

      {error && <p role="alert" className="mb-3 text-sm text-destructive"><FailureText text={error} /></p>}

      {/* A failed read is not an empty archive. */}
      {isError && locations.length === 0 ? (
        <p role="alert" className="py-8 text-sm text-destructive"><FailureText text={errorMessage(loadError, t('locations.archive.loadFailed'))} /></p>
      ) : isLoading ? (
        <GrowLoader variant="block" label={t('locations.archive.loading')} className="py-8" />
      ) : locations.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">{t('locations.archive.empty')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {locations.map((location) => (
            <LocationTile
              key={location.id}
              location={location}
              onOpen={() => { /* nothing to edit here; restore first */ }}
              archived
              onRestore={(l) => { void restore(l); }}
              isRestoring={restoringId === location.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};
